/**
 * SSE event framing: split buffer on blank lines, merge `data:` lines per event.
 * Shared by chat streaming and MCP Streamable HTTP handling.
 *
 * `promptfoo/utils/streamExtractors.js` (`extractSseEvents`) intentionally
 * duplicates this algorithm: the promptfoo eval harness runs as a plain,
 * zero-build Node script (its jest project has no ts-jest/babel transform
 * and its roots are limited to `promptfoo/`), so it cannot `require` this
 * TS module at runtime.
 */
export const createSSEProcessor = (
  buffer: string,
  onChunk: (line: string) => void,
): {
  process: (isFinal: boolean) => string;
} => {
  let localBuffer = buffer;

  const findBoundary = (): { index: number; length: number } | null => {
    const idxCRLF = localBuffer.indexOf('\r\n\r\n');
    const idxLF = localBuffer.indexOf('\n\n');
    if (idxCRLF !== -1 && (idxLF === -1 || idxCRLF < idxLF)) {
      return { index: idxCRLF, length: 4 };
    }
    if (idxLF !== -1) {
      return { index: idxLF, length: 2 };
    }
    return null;
  };

  const process = (isFinal: boolean): string => {
    let boundary = findBoundary();
    while (boundary) {
      const eventChunk = localBuffer.slice(0, boundary.index);
      localBuffer = localBuffer.slice(boundary.index + boundary.length);
      const lines = eventChunk.split(/\r?\n/);
      const dataPayload = lines
        .filter(line => line.startsWith('data:'))
        .map(line => line.replace(/^data:\s?/, ''))
        .join('\n')
        .trim();
      if (dataPayload && dataPayload !== '[DONE]') {
        onChunk(dataPayload);
      }
      boundary = findBoundary();
    }
    if (isFinal && localBuffer.trim()) {
      const lines = localBuffer.split(/\r?\n/);
      const dataPayload = lines
        .filter(line => line.startsWith('data:'))
        .map(line => line.replace(/^data:\s?/, ''))
        .join('\n')
        .trim();
      if (dataPayload && dataPayload !== '[DONE]') {
        onChunk(dataPayload);
      }
      localBuffer = '';
    }
    return localBuffer;
  };

  return { process };
};
